// Organization 生產授權 governance panel (ADR-048 / common!98 specs/19 §1).
//
// Rendered inside the org 360 detail. Read-first: it SHOWS the current grant
// state / version / scope vocabulary / 6-item versioned checklist / audit tuple,
// and offers only the lifecycle moves the backend state machine will accept
// (canTransition mirrors service/productiongrant.go). Every mutation carries a
// mandatory reason + case_id; activate / suspend / revoke require a second
// confirmation. A 409 (optimistic-lock or stale transition) surfaces a
// reload-then-retry prompt rather than blindly overwriting another admin.
//
// ⚠️ Backend (restful!229-!232) is Draft / not yet deployed — this is verified
// against the ratified OpenAPI schema; live integration lands post-deploy.

import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { CopyableId } from '../../components/CopyableId';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { formatTimestampUs, shortId } from '../../lib/format';
import type { AdminOrgDetail } from './organizationsApi';
import {
  PRODUCTION_CHECKLIST_KEYS,
  PRODUCTION_CHECKLIST_LABELS,
  PRODUCTION_GRANT_STATE_LABELS,
  PRODUCTION_SCOPES,
  PRODUCTION_SCOPE_LABELS,
  canTransition,
  productionGrantErrorMessage,
  productionGrantTone,
  setProductionGrant,
} from './productionGrantApi';
import type {
  ProductionChecklistKey,
  ProductionGrant,
  ProductionGrantMutationInput,
  ProductionGrantState,
  ProductionScope,
} from './productionGrantApi';
import './productionGrant.css';

// ADR-048 checklist is versioned so a later policy change never silently
// reinterprets an older approval. Default to the common!98 specs/19 doc version;
// the operator can override per grant.
const DEFAULT_CHECKLIST_VERSION = '1.0.0';

type EditorTarget = 'applied' | 'active';
type PendingKind = 'activate' | 'suspend' | 'revoke';

function fullChecklist(src?: Partial<Record<ProductionChecklistKey, boolean>>): Record<ProductionChecklistKey, boolean> {
  const out = {} as Record<ProductionChecklistKey, boolean>;
  for (const key of PRODUCTION_CHECKLIST_KEYS) out[key] = !!src?.[key];
  return out;
}

export function ProductionGrantPanel({ org, onMutated }: { org: AdminOrgDetail; onMutated: () => void }) {
  const { role } = useAuth();
  const writable = canAdminWrite(role);
  const grant: ProductionGrant | null = org.production_grant ?? null;
  const state = grant?.state;
  const expectedVersion = grant?.version ?? 0;

  // Editor (apply / activate / same-state active refresh) — scope + checklist.
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [scopes, setScopes] = useState<Set<ProductionScope>>(new Set());
  const [checklist, setChecklist] = useState<Record<ProductionChecklistKey, boolean>>(fullChecklist());
  const [checklistVersion, setChecklistVersion] = useState(DEFAULT_CHECKLIST_VERSION);
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');

  // Second-confirmation gate for high-risk commits.
  const [pending, setPending] = useState<PendingKind | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const checklistComplete = useMemo(() => PRODUCTION_CHECKLIST_KEYS.every((k) => checklist[k]), [checklist]);
  const auditReady = reason.trim() !== '' && caseId.trim() !== '';

  function resetForms() {
    setEditorTarget(null);
    setPending(null);
    setReason('');
    setCaseId('');
    setError(null);
  }

  function openEditor(target: EditorTarget) {
    setScopes(new Set(grant?.scopes ?? []));
    setChecklist(fullChecklist(grant?.checklist));
    setChecklistVersion(grant?.checklist_version || DEFAULT_CHECKLIST_VERSION);
    setReason('');
    setCaseId('');
    setError(null);
    setConflict(false);
    setPending(null);
    setEditorTarget(target);
  }

  function openConfirm(kind: PendingKind) {
    setEditorTarget(null);
    setReason('');
    setCaseId('');
    setError(null);
    setConflict(false);
    setPending(kind);
  }

  async function commit(input: ProductionGrantMutationInput) {
    setBusy(true);
    setError(null);
    try {
      await setProductionGrant(org.org_id, input);
      resetForms();
      setConflict(false);
      onMutated();
    } catch (e) {
      const info = productionGrantErrorMessage(e);
      setError(info.text);
      if (info.conflict) setConflict(true);
    } finally {
      setBusy(false);
    }
  }

  function editorMutation(target: ProductionGrantState): ProductionGrantMutationInput {
    return {
      state: target,
      scopes: [...scopes],
      checklist,
      checklist_version: checklistVersion.trim(),
      expected_version: expectedVersion,
      reason: reason.trim(),
      case_id: caseId.trim(),
    };
  }

  function simpleMutation(target: ProductionGrantState): ProductionGrantMutationInput {
    return {
      state: target,
      // suspend/revoke preserve the withdrawn authority; the schema still
      // requires a checklist_version, so echo the stored (or default) one.
      checklist_version: grant?.checklist_version || DEFAULT_CHECKLIST_VERSION,
      expected_version: expectedVersion,
      reason: reason.trim(),
      case_id: caseId.trim(),
    };
  }

  function submitEditor(e: FormEvent) {
    e.preventDefault();
    if (!editorTarget || !auditReady || checklistVersion.trim() === '') return;
    if (editorTarget === 'active') {
      // activation grants production authority — require a second confirm.
      if (!checklistComplete || scopes.size === 0) return;
      setPending('activate');
      return;
    }
    void commit(editorMutation('applied'));
  }

  const tone = productionGrantTone(state);

  return (
    <section className="card pg-panel" aria-label="生產授權 治理">
      <div className="pg-head">
        <div className="pg-head-titles">
          <h3 className="section-title">生產授權 治理</h3>
          <span className="section-meta">ADR-048 · 生產授權（獨立於組織核准 / 帳戶分級）</span>
        </div>
        {grant ? (
          <div className="pg-head-status">
            <span className={`pill pill-dot pill-${tone}`}>{PRODUCTION_GRANT_STATE_LABELS[grant.state]}</span>
            <span className="text-tertiary pg-version">版本 v{grant.version}</span>
          </div>
        ) : (
          <span className="pill pill-neutral">尚未申請</span>
        )}
      </div>

      {conflict && (
        <div className="adm-action-result is-warn pg-conflict" role="alert">
          <span>{error ?? '此授權已被他人修改。'}</span>
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={busy}
            onClick={() => {
              setConflict(false);
              resetForms();
              onMutated();
            }}
          >
            重新載入授權
          </button>
        </div>
      )}
      {error && !conflict && (
        <p className="error-text pg-error" role="alert">
          {error}
        </p>
      )}

      {!grant ? (
        <div className="pg-empty">
          <div className="adm-inline-empty">
            <span className="adm-inline-empty-icon">🔒</span>
            <span>此組織尚未申請 生產授權。組織核准不等於生產授權；未取得 active 授權前，所有生產操作（AID／工廠憑證／Stable 韌體／用戶端 App）一律拒絕。</span>
          </div>
        </div>
      ) : (
        <div className="pg-body">
          {/* ── 稽核 tuple + 生命週期時間戳 ── */}
          <dl className="adm-kv pg-kv">
            <dt>checklist 版本</dt>
            <dd className="adm-mono">{grant.checklist_version || '—'}</dd>
            <dt>最後操作者</dt>
            <dd>{grant.last_actor ? <CopyableId value={grant.last_actor} display={shortId(grant.last_actor)} /> : '—'}</dd>
            <dt>最後原因</dt>
            <dd>{grant.last_reason || '—'}</dd>
            <dt>最後案件</dt>
            <dd className="adm-mono">{grant.last_case_id || '—'}</dd>
            <dt>更新時間</dt>
            <dd className="adm-mono">{formatTimestampUs(grant.updated_at, { second: undefined })}</dd>
          </dl>

          <ul className="pg-timeline">
            {grant.applied_at ? (
              <li>
                <span className="pg-timeline-label">申請</span>
                <span className="adm-mono">{formatTimestampUs(grant.applied_at, { second: undefined })}</span>
                {grant.applied_by && <span className="text-tertiary">by {shortId(grant.applied_by)}</span>}
              </li>
            ) : null}
            {grant.granted_at ? (
              <li>
                <span className="pg-timeline-label pg-timeline-good">核准啟用</span>
                <span className="adm-mono">{formatTimestampUs(grant.granted_at, { second: undefined })}</span>
                {grant.granted_by && <span className="text-tertiary">by {shortId(grant.granted_by)}</span>}
              </li>
            ) : null}
            {grant.suspended_at ? (
              <li>
                <span className="pg-timeline-label pg-timeline-warn">停權</span>
                <span className="adm-mono">{formatTimestampUs(grant.suspended_at, { second: undefined })}</span>
                {grant.suspended_by && <span className="text-tertiary">by {shortId(grant.suspended_by)}</span>}
              </li>
            ) : null}
            {grant.revoked_at ? (
              <li>
                <span className="pg-timeline-label pg-timeline-danger">撤銷</span>
                <span className="adm-mono">{formatTimestampUs(grant.revoked_at, { second: undefined })}</span>
                {grant.revoked_by && <span className="text-tertiary">by {shortId(grant.revoked_by)}</span>}
              </li>
            ) : null}
          </ul>

          {/* ── scope 詞彙(固定 4 項,標示已授權)── */}
          <div className="pg-section">
            <div className="pg-section-head">
              <span className="pg-section-title">授權範圍 scope</span>
              <span className="text-tertiary">{grant.state === 'active' ? '目前生效' : '已記錄（非 active 時不生效）'}</span>
            </div>
            <ul className="pg-scope-list">
              {PRODUCTION_SCOPES.map((scope) => {
                const on = grant.scopes.includes(scope);
                return (
                  <li key={scope} className={`pg-scope ${on ? 'is-on' : ''}`}>
                    <span className="pg-scope-mark" aria-hidden>
                      {on ? '●' : '○'}
                    </span>
                    <span>{PRODUCTION_SCOPE_LABELS[scope]}</span>
                    <code className="adm-mono text-tertiary pg-scope-code">{scope}</code>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ── 6 項 versioned checklist(active 需全過)── */}
          <div className="pg-section">
            <div className="pg-section-head">
              <span className="pg-section-title">核准檢核清單（6 項）</span>
              <span className={`text-tertiary ${PRODUCTION_CHECKLIST_KEYS.every((k) => grant.checklist?.[k]) ? '' : 'pg-incomplete'}`}>
                啟用（active）需全數通過
              </span>
            </div>
            <ul className="pg-checklist">
              {PRODUCTION_CHECKLIST_KEYS.map((key) => {
                const ok = !!grant.checklist?.[key];
                return (
                  <li key={key} className="pg-check-item">
                    <span className={`pill pill-dot pill-${ok ? 'good' : 'neutral'}`}>{ok ? '通過' : '未通過'}</span>
                    <span className={ok ? '' : 'text-secondary'}>{PRODUCTION_CHECKLIST_LABELS[key]}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* ── 管理動作 ── */}
      {writable ? (
        editorTarget ? (
          <GrantEditor
            target={editorTarget}
            scopes={scopes}
            checklist={checklist}
            checklistVersion={checklistVersion}
            reason={reason}
            caseId={caseId}
            busy={busy}
            auditReady={auditReady}
            checklistComplete={checklistComplete}
            onToggleScope={(scope) =>
              setScopes((prev) => {
                const next = new Set(prev);
                if (next.has(scope)) next.delete(scope);
                else next.add(scope);
                return next;
              })
            }
            onToggleCheck={(key) => setChecklist((prev) => ({ ...prev, [key]: !prev[key] }))}
            onChecklistVersion={setChecklistVersion}
            onReason={setReason}
            onCaseId={setCaseId}
            onSubmit={submitEditor}
            onCancel={() => resetForms()}
          />
        ) : (
          <div className="pg-actions">
            {!grant && (
              <button type="button" className="btn-primary" onClick={() => openEditor('applied')}>
                申請 生產授權
              </button>
            )}
            {state === 'applied' && (
              <>
                <button type="button" className="btn-primary" onClick={() => openEditor('active')}>
                  核准啟用
                </button>
                <button type="button" className="btn-ghost" onClick={() => openEditor('applied')}>
                  更新申請內容
                </button>
                {canTransition('applied', 'revoked') && (
                  <button type="button" className="btn-danger" onClick={() => openConfirm('revoke')}>
                    撤銷申請
                  </button>
                )}
              </>
            )}
            {state === 'active' && (
              <>
                <button type="button" className="btn-ghost" onClick={() => openEditor('active')}>
                  更新 scope／檢核
                </button>
                <button type="button" className="btn-ghost pg-btn-warn" onClick={() => openConfirm('suspend')}>
                  停權
                </button>
                <button type="button" className="btn-danger" onClick={() => openConfirm('revoke')}>
                  撤銷
                </button>
              </>
            )}
            {state === 'suspended' && (
              <>
                <button type="button" className="btn-primary" onClick={() => openEditor('active')}>
                  恢復啟用
                </button>
                <button type="button" className="btn-danger" onClick={() => openConfirm('revoke')}>
                  撤銷
                </button>
              </>
            )}
            {state === 'revoked' && (
              <button type="button" className="btn-primary" onClick={() => openEditor('applied')}>
                重新申請
              </button>
            )}
          </div>
        )
      ) : (
        <p className="text-tertiary pg-readonly">唯讀角色：僅可檢視 生產授權 狀態，無法申請、核准、停權或撤銷。</p>
      )}

      {/* ── 二次確認 ── */}
      {pending === 'activate' && (
        <ConfirmDialog
          title="核准啟用 生產授權"
          tone="primary"
          confirmLabel="確認核准啟用"
          busy={busy}
          error={error}
          warning={
            <span>
              啟用後此組織將取得下列生產權限：{[...scopes].map((s) => PRODUCTION_SCOPE_LABELS[s]).join('、') || '（未選）'}。
              生產服務會即時重查此授權。
            </span>
          }
          body={
            <>
              <p style={{ margin: 0 }}>
                版本 v{expectedVersion} → v{expectedVersion + 1}；checklist 版本 {checklistVersion.trim() || '—'}，6 項檢核全數通過。
              </p>
              <p className="text-tertiary" style={{ margin: '8px 0 0' }}>
                原因：{reason.trim()}　案件：{caseId.trim()}
              </p>
            </>
          }
          onConfirm={() => void commit(editorMutation('active'))}
          onCancel={() => setPending(null)}
        />
      )}
      {pending === 'suspend' && (
        <ConfirmDialog
          title="停權 生產授權"
          tone="warn"
          confirmLabel="確認停權"
          busy={busy}
          error={error}
          confirmDisabled={!auditReady}
          warning={<span>停權將撤銷登入、API key、發布與工廠操作，但保留組織資產、已售裝置基本運作與稽核紀錄；不會讓客戶裝置變磚。</span>}
          body={
            <ReasonCaseFields reason={reason} caseId={caseId} onReason={setReason} onCaseId={setCaseId} disabled={busy} />
          }
          onConfirm={() => void commit(simpleMutation('suspended'))}
          onCancel={() => setPending(null)}
        />
      )}
      {pending === 'revoke' && (
        <ConfirmDialog
          title="撤銷 生產授權"
          tone="danger"
          confirmLabel="確認撤銷"
          busy={busy}
          error={error}
          confirmDisabled={!auditReady}
          warning={
            <span>
              撤銷後 API key／cache／session 立即失效，所有生產操作即刻被擋；需重新「申請」才能再次啟用。既有客戶裝置不會被刪除或重置。
            </span>
          }
          body={
            <ReasonCaseFields reason={reason} caseId={caseId} onReason={setReason} onCaseId={setCaseId} disabled={busy} />
          }
          onConfirm={() => void commit(simpleMutation('revoked'))}
          onCancel={() => setPending(null)}
        />
      )}
    </section>
  );
}

function ReasonCaseFields({
  reason,
  caseId,
  onReason,
  onCaseId,
  disabled,
}: {
  reason: string;
  caseId: string;
  onReason: (v: string) => void;
  onCaseId: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="pg-reason-fields">
      <label className="adm-field">
        <span className="text-tertiary adm-field-label">原因（稽核必填）</span>
        <input value={reason} onChange={(e) => onReason(e.target.value)} disabled={disabled} placeholder="如：安全事件停權、合約終止撤銷" />
      </label>
      <label className="adm-field">
        <span className="text-tertiary adm-field-label">案件編號（稽核必填）</span>
        <input value={caseId} onChange={(e) => onCaseId(e.target.value)} disabled={disabled} placeholder="CASE-…" />
      </label>
    </div>
  );
}

function GrantEditor({
  target,
  scopes,
  checklist,
  checklistVersion,
  reason,
  caseId,
  busy,
  auditReady,
  checklistComplete,
  onToggleScope,
  onToggleCheck,
  onChecklistVersion,
  onReason,
  onCaseId,
  onSubmit,
  onCancel,
}: {
  target: EditorTarget;
  scopes: Set<ProductionScope>;
  checklist: Record<ProductionChecklistKey, boolean>;
  checklistVersion: string;
  reason: string;
  caseId: string;
  busy: boolean;
  auditReady: boolean;
  checklistComplete: boolean;
  onToggleScope: (scope: ProductionScope) => void;
  onToggleCheck: (key: ProductionChecklistKey) => void;
  onChecklistVersion: (v: string) => void;
  onReason: (v: string) => void;
  onCaseId: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
}) {
  const activating = target === 'active';
  const title = activating ? '核准啟用 / 更新 scope 與檢核' : '申請 / 更新 生產授權';
  const submitLabel = activating ? '核准啟用…' : '送出申請';
  // active requires the full checklist + ≥1 scope (mirrors the backend guard).
  const submitDisabled =
    busy || !auditReady || checklistVersion.trim() === '' || (activating && (!checklistComplete || scopes.size === 0));

  return (
    <form className="pg-editor" onSubmit={onSubmit}>
      <div className="pg-editor-head">
        <h4 className="pg-editor-title">{title}</h4>
        {activating && (
          <span className="text-tertiary">啟用需 6 項檢核全通過且至少 1 個範圍</span>
        )}
      </div>

      <fieldset className="pg-fieldset">
        <legend>授權範圍 scope</legend>
        <div className="pg-check-grid">
          {PRODUCTION_SCOPES.map((scope) => (
            <label key={scope} className="pg-check-label">
              <input type="checkbox" checked={scopes.has(scope)} onChange={() => onToggleScope(scope)} disabled={busy} />
              <span>{PRODUCTION_SCOPE_LABELS[scope]}</span>
              <code className="adm-mono text-tertiary">{scope}</code>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="pg-fieldset">
        <legend>核准檢核清單</legend>
        <div className="pg-check-grid">
          {PRODUCTION_CHECKLIST_KEYS.map((key) => (
            <label key={key} className="pg-check-label">
              <input type="checkbox" checked={checklist[key]} onChange={() => onToggleCheck(key)} disabled={busy} />
              <span>{PRODUCTION_CHECKLIST_LABELS[key]}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="pg-editor-fields">
        <label className="adm-field pg-editor-cv">
          <span className="text-tertiary adm-field-label">查核清單版本</span>
          <input value={checklistVersion} onChange={(e) => onChecklistVersion(e.target.value)} disabled={busy} placeholder="1.0.0" />
        </label>
        <label className="adm-field">
          <span className="text-tertiary adm-field-label">原因（稽核必填）</span>
          <input value={reason} onChange={(e) => onReason(e.target.value)} disabled={busy} placeholder="如：完成合約與安全審核" />
        </label>
        <label className="adm-field">
          <span className="text-tertiary adm-field-label">案件編號（稽核必填）</span>
          <input value={caseId} onChange={(e) => onCaseId(e.target.value)} disabled={busy} placeholder="CASE-…" />
        </label>
      </div>

      <div className="pg-editor-actions">
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
          取消
        </button>
        <button type="submit" className="btn-primary" disabled={submitDisabled}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
